<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useConfigurationsStore } from "../../stores";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast, useUtilities } from "@/composables";

interface SearchResult {
  no: string
  name: string
  type: string
  status?: string
  createdAt?: string
}

const store = useConfigurationsStore();
const { errorToast } = useToast()
const { appendQueryParams } = useUtilities()

const showCreate = ref(false)
const searchName = ref<string | null>(null)
const searchError = ref<string | null>(null)
const loading = ref(false)
const spinner = ref(false)
const file = ref<File | null>(null)
const names = ref<SearchResult[]>([])

const getSearchResults = computed(() => store.searchResults);
const getNameFileUpload = computed(() => store.nameFileUpload);
const getMessageError = computed(() => store.searchError);

const handleFileChange = (event: Event, fieldName: string) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    file.value = target.files[0]
  }
}

const openForm = () => {
  showCreate.value = true
}

const searchPhrase = () => {
  searchError.value = ""
  loading.value = true
  store.doSearch(appendQueryParams({ name: searchName.value, scope: "" }));
}

const saveUpload = () => {
  if (!file.value) return

  const formData = new FormData()
  formData.set("file", file.value)
  spinner.value = true
  store.uploadBusinessNames(formData);
}

watch(getSearchResults, (data) => {
  if (data) {
    names.value = data
    loading.value = false
  }
})

watch(getNameFileUpload, (data) => {
  if (data) {
    names.value = data
    loading.value = false
    showCreate.value = false
    spinner.value = false
  }
})

watch(getMessageError, (error) => {
  if (error) {
    loading.value = false
    searchError.value = error.message
    spinner.value = false
    errorToast("Error", error.message)
  }
})
</script>

<template>
  <div class="flex flex-col items-center py-8 px-4">
    <div class="w-full" style="max-width: 70%">
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Quick Name Search</h2>
        <p class="text-sm text-gray-500 mt-1">Search for registered business names or upload an Excel file to bulk-check
          name availability across the registry.</p>
      </div>
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <form class="space-y-4" @submit.prevent="searchPhrase">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Search Name</label>
            <div class="relative">
              <input
                type="text"
                v-model="searchName"
                placeholder="Search Name"
                @keydown.enter="searchPhrase"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              />
              <i class="fas fa-search absolute right-3 top-3 text-gray-400"></i>
            </div>
          </div>
          <div class="border-t border-gray-100 pt-2 flex justify-end gap-2">
            <button
              @click="openForm"
              type="button"
              class="btn-sm-primary">
              <i class="fa fa-plus"></i>
              <span>Add Names</span>
            </button>
            <button type="submit" class="btn-primary">
              <i class="fas fa-search mr-1"></i>
              Search
            </button>
          </div>
        </form>
      </div>

      <!-- Add Names Modal -->
    <AppModal v-model="showCreate" title="Add Names" hide-footer>
      <div class="p-4">
        <form @submit.prevent="saveUpload">
          <div class="mb-4">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <i class="fa-solid fa-file-excel mr-1"></i>
              Upload Excel File
            </label>
            <input
              type="file"
              @change="handleFileChange($event, 'file')"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div class="flex justify-end">
            <button
              class="btn-primary"
              type="submit"
              :disabled="spinner"
            >
              <i class="fa-solid fa-upload mr-1"></i>
              <span v-if="!spinner">Upload</span>
              <span v-else>Uploading...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Loading Bar -->
    <div class="mb-4" v-if="loading">
      <div class="w-full bg-gray-200 rounded-full h-1">
        <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
      </div>
    </div>

    <!-- Results Table -->
    <div class="bg-white rounded border border-gray-200 overflow-hidden flex-1">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">BRN/RN</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Entity Type</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(list, idx) in names"
              :key="idx"
              class="hover:bg-gray-50 transition"
              :class="idx % 2 === 0 ? '' : 'bg-gray-10'"
            >
              <td class="py-3 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-blue font-bold">{{ list.no }}</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-gray-900 font-medium">{{ list.name }}</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-gray-600 capitalize">{{ list.type }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
